/**
 * Invitation Service
 *
 * Client-side service for the family invitation acceptance flow.
 * Calls Cloud Functions (Admin SDK) because:
 * - verifyInvitation: needs to read profiles without authentication
 * - acceptInvitation: needs to write to the pilot's profile (new member has no access)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../config/firebase';

const functions = getFunctions(app);

// ── Types ────────────────────────────────────────────────────────

export interface VerifiedInvitation {
  memberName: string;
  memberLastName: string | null;
  familyCode: string;
  profileOwnerName: string;
  profileId: string;
  role: string;
  relationship: string | null;
  email: string | null;
}

interface VerifyResponse extends VerifiedInvitation {}

interface AcceptResponse {
  success: boolean;
}

// ── Service ──────────────────────────────────────────────────────

class InvitationService {
  /**
   * Verify an invitation link (works without authentication).
   * Validates the invitation exists, is pending, and not expired.
   * Also tracks the view count server-side.
   */
  async verify(invitationId: string): Promise<VerifiedInvitation> {
    const verifyFn = httpsCallable<{ invitationId: string }, VerifyResponse>(
      functions,
      'verifyInvitation'
    );
    const result = await verifyFn({ invitationId });
    return result.data;
  }

  /**
   * Accept an invitation (requires authentication).
   * Marks invitation as accepted and adds the calling user
   * as a family member on the pilot's profile.
   */
  async accept(invitationId: string, profileId: string): Promise<void> {
    const acceptFn = httpsCallable<{ invitationId: string; profileId: string }, AcceptResponse>(
      functions,
      'acceptInvitation'
    );
    await acceptFn({ invitationId, profileId });
  }
}

export const invitationService = new InvitationService();
